import {useTheme} from '@mui/material';
import {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {useHistory, useLocation} from 'react-router-dom';
import {ServiceContext} from './components/Services';

export const usePermissions = (permissions = null) => {
  const user = useSelector(state => state['auth'].user);
  const [hasPermission, setHasPermission] = useState(false);

  const userCan = useCallback((permissions, can = null, cant = null) => {
    for (let permission of permissions) {
      if (user.permissions[`can_${permission}`]) {
        return can;
      }
    }
    return cant;
  }, [user]);

  useEffect(() => {
    if (permissions) {
      setHasPermission(userCan(permissions, true, false));
    } else {
      setHasPermission(false);
    }
  }, [permissions, user, userCan])

  return {hasPermission, user, userCan}
}

export const useContactTypes = () => {
  const theme = useTheme();
  return useMemo(() => [
    {title: 'All', singular: 'All', key: 'all', colour: theme.palette['contactType'].all.main},
    {title: 'Customers', singular: 'Customer', key: 'customers', colour: theme.palette['contactType'].customer.main},
    {title: 'Suppliers', singular: 'Supplier', key: 'suppliers', colour: theme.palette['contactType'].supplier.main},
    {title: 'Prospects', singular: 'Prospect', key: 'prospects', colour: theme.palette['contactType'].prospect.main},
    {
      title: 'Transport',
      singular: 'Transport',
      key: 'transporters',
      colour: theme.palette['contactType'].transporter.main
    },
  ], [theme]);
}

export const useMaterialBubbles = () => {
  const services = useContext(ServiceContext);
  const theme = useTheme();
  const [materials, setMaterials] = useState([]);

  useEffect(() => {
    services.material.getMaterials({}, 1, 500).then((response) => setMaterials(response.data.map(({id, name}) => (
      {id, label: name, colour: theme['palette'].secondary.main}
    )))).catch(() => setMaterials([]));
  }, [services, theme]);

  return materials;
}

export const useMaterialOptionBubbles = () => {
  const services = useContext(ServiceContext);
  const theme = useTheme();
  const [formats, setFormats] = useState([]);
  const [colours, setColours] = useState([]);

  useEffect(() => {
    services.attribute.materialFormat.getAttributes({}, 1, 500).then((response) => {
      setFormats([
        ...response.map(({id, name}) => (
          {id, label: name, colour: theme['palette'].secondary.main}
        )),
        {id: 'O', label: 'Other', colour: theme['palette'].secondary.main}
      ]);
      services.attribute.materialColour.getAttributes({}, 1, 500).then((response) => setColours([
        ...response.map(({id, name}) => ({id, label: name, colour: theme['palette'].secondary.main})),
        {id: 'O', label: 'Other', colour: theme['palette'].secondary.main}
      ])).catch(() => setColours([]));
    }).catch(() => setFormats([]) || setColours([]));
  }, [services, theme]);

  return {formats, colours};
}


export const useOrderStatuses = () => {
  const theme = useTheme();
  return useMemo(() => [
    {title: 'In Progress', key: 'ongoing', colour: theme.palette['orderStatus'].ongoing.main},
    {title: 'Pending', key: 'pending', colour: theme.palette['orderStatus'].pending.main},
    {title: 'Locked', key: 'locked', colour: theme.palette['orderStatus'].locked.main},
    {title: 'Delivered', key: 'delivered', colour: theme.palette['orderStatus'].delivered.main},
    {title: 'Weighed', key: 'weighed', colour: theme.palette['orderStatus'].weighed.main},
    {title: 'Quality Checked', key: 'checked', colour: theme.palette['orderStatus'].checked.main},
    {title: 'Invoiced', key: 'invoiced', colour: theme.palette['orderStatus'].invoiced.main},
    {title: 'On Hold', key: 'held', colour: theme.palette['orderStatus'].held.main},
    {title: 'Cancelled', key: 'cancelled', colour: theme.palette['orderStatus'].cancelled.main},
    {title: 'All', key: 'all', colour: theme.palette['orderStatus'].all.main},
  ], [theme]);
}

export const useInboxStatuses = () => {
  const theme = useTheme();
  return useMemo(() => [
    {title: 'Unread', key: 'unread', colour: theme.palette['inboxStatus'].unread.main},
    {title: 'Open', key: 'open', colour: theme.palette['inboxStatus'].open.main},
    {title: 'Closed', key: 'closed', colour: theme.palette['inboxStatus'].closed.main},
    {title: 'All', key: 'all', colour: theme.palette['inboxStatus'].all.main},
  ], [theme]);
}

export const useAccountFormStatuses = () => {
  const theme = useTheme();
  return useMemo(() => [
    {title: 'Created', key: 'created', colour: theme.palette['accountForm'].created.main},
    {title: 'Sent', key: 'sent', colour: theme.palette['accountForm'].sent.main},
    {title: 'Completed', key: 'completed', colour: theme.palette['accountForm'].completed.main},
    {title: 'All', key: 'all', colour: theme.palette['accountForm'].all.main},
  ], [theme]);
}

export const useQuery = (defaultValues) => {
  const [values, setValues] = useState({...defaultValues});
  const history = useHistory();
  const {search} = useLocation();
  const [initialised, setInitialised] = useState(false);

  useEffect(() => {
    const updated = {...defaultValues};
    const params = new URLSearchParams(search);
    params.forEach((value, key) => {
      updated[key] = value;
    });
    setValues(updated);
    setInitialised(true);
  }, [defaultValues, search])

  const handleChange = useCallback((change) => {
    const params = new URLSearchParams(search);
    for (let key of Object.keys(change)) {
      if (change[key] == null || change[key] === defaultValues[key]) {
        if (params.has(key)) {
          params.delete(key);
        }
      } else {
        params.set(key, change[key]);
      }
    }
    history.push({search: params.toString()});
  }, [defaultValues, history, search]);

  const handlePageChange = useCallback(([query, page, pageSize]) => {
    const changes = {
      page: `${page}`,
      pageSize: `${pageSize}`,
      search: query?.filter?.search ?? ''
    };
    if (Object.keys(changes).length > 0) {
      handleChange(changes);
    }
  }, [handleChange])

  return {values, changeQuery: handleChange, changePage: handlePageChange, initialised};
}

export const useTaskStatuses = () => {
  const theme = useTheme();
  return useMemo(() => [
    {title: 'Pending', key: 'pending', colour: theme.palette['taskStatus'].pending.main},
    {title: 'Overdue', key: 'overdue', colour: theme.palette['taskStatus'].overdue.main},
    {title: 'Completed', key: 'completed', colour: theme.palette['taskStatus'].completed.main},
    {title: 'All', key: 'all', colour: theme.palette['taskStatus'].all.main},
  ], [theme]);
}
